import FormItem from "components/common/FormItem";
import {
  ApplyStatusType,
  JOHOKU_ACCOUNT,
  JOHOKU_ACCOUNT_TYPE,
  getAnnualSalesTypeName,
  getCapitalTypeName,
  getEmployeeNumTypeName,
  getNyuMonthTypeName,
  NyuMonthType,
} from "enums";
import {
  CorpBankModel,
  CorpInfoModel,
  IndustryMstModel,
  JohokuBranchMstModel,
} from "models";
import React from "react";
import AccountForms from "./forms/AccountForms";
import IndustryMstSelect from "./forms/IndustryMstSelect";
import PrefectureSelect from "./forms/PrefectureSelect";

import { Utils } from "utils";
import DateInputForm from "./forms/DateInputForm";
import { TOOLTIP_DATA } from "enums/tooltip_data";
import AppTooltip from "../../common/AppTooltip";

type PROPS = {
  isUpdate: boolean;
  data?: CorpInfoModel;
  setData?: (value: CorpInfoModel) => void;
  industryMst: IndustryMstModel[];
  branchMst: JohokuBranchMstModel[];
  onAddressSearch?: () => void;
};

const CorpInfoForms: React.FC<PROPS> = (props) => {
  const data = props.data;
  const setData = props.setData ?? (() => {});
  if (!data) {
    return <></>;
  }
  const disabled = data.apply_status === ApplyStatusType._0_WAIT;

  function isValidDate(dateString : any) {
    if (typeof dateString !== 'string' || !/^\d{4}\/\d{2}\/\d{2}$/.test(dateString)) {
      return false;
    }
    return !isNaN(Date.parse(dateString));
  }

  const onAddressSearch = () => {
    if (props.onAddressSearch) {
      props.onAddressSearch();
    }
  };

  // 口座情報の追加
  const onAddAccount = () => {
    data.bank_account_list.push({
      bank_cd: JOHOKU_ACCOUNT.bank_cd,
      bank_name: JOHOKU_ACCOUNT.bank_name,
      branch_cd: "",
      branch_name: "",
      account_type: 1,
      account_no: "",
      account_name: "",
      johoku_account_type: JOHOKU_ACCOUNT_TYPE.HAS_ACCOUNT,
    });
    if (props.setData) {
      props.setData({ ...data });
    }
  };

  // 口座情報の削除
  const onRemoveAccount = (target: CorpBankModel) => {
    const idx = data.bank_account_list.indexOf(target);
    data.bank_account_list.splice(idx, 1);
    if (props.setData) {
      props.setData({ ...data });
    }
  };

  const options = [...Array(28)].map((v, i) => (
    <option key={i} value={i + 1}>
      {i + 1}
    </option>
  ));
  options.push(
    <option key={31} value={31}>
      月末
    </option>
  );

  return (
    <>
      <div className="mt-4 flex flex-col gap-2">
        <div className="pt-4 border-b font-bold">会社名登録</div>
        <FormItem label="会社名" required>
          <input
            className="w-full"
            value={data.corp_name}
            onChange={(e) => {
              data.corp_name = e.target.value;
              setData({ ...data });
            }}
            disabled={disabled}
          />
        </FormItem>
        <FormItem label="代表者名" required>
          <input
            className="w-full"
            value={data.representative_name}
            onChange={(e) => {
              data.representative_name = e.target.value;
              setData({ ...data });
            }}
            disabled={disabled}
          />
        </FormItem>
        <FormItem label="法人個人区分" required>
          <div className="flex">
            <label className="cursor-pointer">
              <input
                type="radio"
                name="is_corp"
                checked={data.is_personal === false}
                onChange={(e) => {
                  data.is_personal = false;
                  setData({ ...data });
                }}
                disabled={disabled || props.isUpdate}
              />
              <span className="ml-2">法人</span>
            </label>
            <label className="cursor-pointer ml-8">
              <input
                type="radio"
                name="is_corp"
                checked={data.is_personal === true}
                onChange={(e) => {
                  data.is_personal = true;
                  setData({ ...data });
                }}
                disabled={disabled || props.isUpdate}
              />
              <span className="ml-2">個人</span>
            </label>
          </div>
        </FormItem>
        {!data.is_personal && (
          <>
            <FormItem
              label="法人番号"
              required
              tooltip={TOOLTIP_DATA.CORPS.corp_no}
            >
              <input
                className="w-full"
                value={data.corp_no}
                onChange={(e) => {
                  data.corp_no = e.target.value;
                  setData({ ...data });
                }}
                disabled={disabled || props.isUpdate}
              />
            </FormItem>
            <FormItem>
              <div className="underline text-jhk-blue my-2">
                <a
                  href="https://www.houjin-bangou.nta.go.jp/"
                  target="_blank"
                  rel="noreferrer"
                >
                  法人番号検索はこちら
                </a>
              </div>
            </FormItem>
          </>
        )}
        <FormItem
          label="適格請求書発行事業者登録番号"
          tooltip={TOOLTIP_DATA.CORPS.invoice_no}
        >
          <div className="flex justify-between items-center gap-4">
            <div className="ml-4 text-4xl font-bold">T</div>
            <div className="w-full">
              <input
                className="w-full"
                value={data.invoice_no.replaceAll("T", "")}
                onChange={(e) => {
                  data.invoice_no = `T` + e.target.value.replaceAll("T", "");
                  setData({ ...data });
                }}
                disabled={disabled}
                type="tel"
              />
            </div>
          </div>
        </FormItem>
        <FormItem>
          <div className="underline text-jhk-blue my-2">
            <a
              href="https://www.invoice-kohyo.nta.go.jp/index.html"
              target="_blank"
              rel="noreferrer"
            >
              登録番号検索はこちら
            </a>
          </div>
        </FormItem>
        <FormItem label="業種" required>
          <IndustryMstSelect
            mst={props.industryMst}
            data={data}
            disabled={disabled}
          />
        </FormItem>
      </div>

      <div className="mt-8 flex flex-col gap-2">
        <div className="pt-4 border-b font-bold">基本情報登録</div>
        <FormItem
          label="郵便番号"
          required
        >
          <div className="flex items-center ">
            <input
              className=" w-40"
              value={data.post_no1}
              onChange={(e) => {
                data.post_no1 = e.target.value;
                setData({ ...data, post_no1: data.post_no1 });
              }}
              type="tel"
              disabled={disabled}
            />
            <span className="items-center mr-3 ml-3 font-bold ">─</span>
            <input
              className="w-50 mr-2"
              value={data.post_no2}
              onChange={(e) => {
                data.post_no2 = e.target.value;
                setData({ ...data, post_no2: data.post_no2 });
              }}
              type="tel"
              disabled={disabled}
            />

            <button
              className={`${
                disabled || data.post_no1 === "" || data.post_no2 === ""
                  ? "secondary-disabled px-4 py-2"
                  : "primary px-4 py-2 whitespace-nowrap"
              }`}
              disabled={
                disabled || data.post_no1 === "" || data.post_no2 === ""
              }
              onClick={onAddressSearch}
            >
              住所自動入力
            </button>
          </div>
        </FormItem>
        <FormItem label="都道府県" required>
          <PrefectureSelect data={data} disabled={disabled} />
        </FormItem>
        <FormItem label="市区町村" required>
          <input
            className="w-full"
            value={data.citytown}
            onChange={(e) => {
              data.citytown = e.target.value;
              setData({ ...data });
            }}
            disabled={disabled}
          />
        </FormItem>
        <FormItem label="住所(番地・建物名等)" required>
          <input
            className="w-full"
            value={data.address}
            onChange={(e) => {
              data.address = e.target.value;
              setData({ ...data });
            }}
            disabled={disabled}
          />
        </FormItem>
        <FormItem label="電話番号" required tooltip={TOOLTIP_DATA.CORPS.tel_no}>
          <input
            className="w-full"
            value={data.tel_no}
            onChange={(e) => {
              data.tel_no = e.target.value;
              setData({ ...data });
            }}
            type="tel"
            disabled={disabled}
          />
        </FormItem>
        <FormItem label="FAX番号" tooltip={TOOLTIP_DATA.CORPS.fax_no}>
          <input
            className="w-full"
            value={data.fax_no}
            onChange={(e) => {
              data.fax_no = e.target.value;
              setData({ ...data });
            }}
            type="tel"
            disabled={disabled}
          />
        </FormItem>
        <FormItem label="会社メールアドレス" required tooltip={TOOLTIP_DATA.CORPS.corp_mail_address}>
          <input
            className="w-full"
            value={data.corp_mail_address}
            onChange={(e) => {
              data.corp_mail_address = e.target.value;
              setData({ ...data });
            }}
            disabled={disabled}
            type="email"
          />
        </FormItem>
        <FormItem label="会社URL" tooltip={TOOLTIP_DATA.CORPS.corp_url}>
          <input
            className="w-full"
            value={data.corp_url}
            onChange={(e) => {
              data.corp_url = e.target.value;
              setData({ ...data });
            }}
            disabled={disabled}
            type="url"
          />
        </FormItem>
        <FormItem label="設立年月日" required>
          <DateInputForm
            className="w-full"
            value={Utils.Date.format3(data.establishment_date)}
            onChange={(value) => {
              if (isValidDate(value)) {
                if (value === null) {
                  data.establishment_date = "";
                  setData({ ...data });
                } else {
                  const formattedDate = (value as any).replace(/\//g, '-');
                  data.establishment_date = formattedDate;
                  setData({ ...data });
                }
              }
            }}
            disabled={disabled}
          />
        </FormItem>
        <FormItem label="資本金" required>
          <select
            value={data.capital_cd}
            onChange={(e) => {
              if (e.target.value === "") {
                data.capital_cd = "";
                setData({ ...data });
                return;
              }
              const value = parseInt(e.target.value);
              if (isNaN(value)) {
                return;
              }
              data.capital_cd = value;
              setData({ ...data });
            }}
            disabled={disabled}
          >
            <option value="">選択してください</option>
            {[...Array(6)].map((v, i) => {
              return (
                <option key={i} value={i}>
                  {getCapitalTypeName(i)}
                </option>
              );
            })}
          </select>
        </FormItem>
        <FormItem label="従業員数" required>
          <select
            value={data.employees_number_cd}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                data.employees_number_cd = value as any;
                setData({ ...data });
              }
              const n = parseInt(value);
              if (isNaN(n)) {
                return;
              }
              data.employees_number_cd = n;
              setData({ ...data });
            }}
            disabled={disabled}
          >
            <option value="">選択してください</option>
            {[...Array(9)].map((v, i) => {
              return (
                <option key={i} value={i + 1}>
                  {getEmployeeNumTypeName(i + 1)}
                </option>
              );
            })}
          </select>
        </FormItem>
        <FormItem label="年商" required>
          <select
            value={data.yearly_sales_cd}
            onChange={(e) => {
              if (e.target.value === "") {
                data.yearly_sales_cd = "";
                setData({ ...data });
                return;
              }
              const value = parseInt(e.target.value);
              if (isNaN(value)) {
                return;
              }
              data.yearly_sales_cd = value;
              setData({ ...data });
            }}
            disabled={disabled}
          >
            <option value="">選択してください</option>
            {[...Array(8)].map((v, i) => {
              return (
                <option key={i} value={i}>
                  {getAnnualSalesTypeName(i)}
                </option>
              );
            })}
          </select>
        </FormItem>
        <FormItem
          label="希望発注限度額"
          required
          tooltip={TOOLTIP_DATA.CORPS.order_limit}
        >
          <div className="flex items-center gap-2">
            <input
              value={Utils.addComma(data.order_limit, false)}
              onKeyDown={(e) => {
                data.order_limit = (e.target as any).value.replace(/\D/g, "");
                setData({ ...data });
              }}
              onKeyUp={(e) => {
                data.order_limit = (e.target as any).value.replace(/\D/g, "");
                setData({ ...data });
              }}
              onBlur={(e) => {
                const numericValue = (e.target.value as any).replace(
                  /[^-\d,]/g,
                  ""
                );
                const validNumericValue = numericValue.match(/-?[\d,]+/g);

                if (validNumericValue) {
                  const numericString = validNumericValue[0].replace(/,/g, "");
                  data.order_limit = numericString;
                } else {
                  data.order_limit = "";
                }
                setData({ ...data });
              }}
              onChange={(e) => {
                if (e.target.value === "") {
                  data.order_limit = "";
                  setData({ ...data });
                } else {
                  data.order_limit = e.target.value as any;
                  setData({ ...data });
                }
              }}
              type="tel"
              disabled={disabled}
            />
            <span>万円</span>
          </div>
        </FormItem>
        <FormItem label="受注時入金条件">
          <div className="flex gap-12">
            <span>翌月</span>
            <span>31日(月末)</span>
          </div>
        </FormItem>
        <FormItem label="発注時支払条件" tooltip={TOOLTIP_DATA.CORPS.nyu_month}>
          <div className="flex items-center gap-4">
            <select
              value={data.nyu_month}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (isNaN(value)) {
                  return;
                }
                data.nyu_month = value;
                setData({ ...data });
              }}
              disabled={disabled || props.isUpdate}
            >
              {[
                NyuMonthType._2_YOKU,
                NyuMonthType._3_YOKUYOKU,
                NyuMonthType._4_3MONTH,
              ].map((v, i) => {
                return (
                  <option key={i} value={v}>
                    {getNyuMonthTypeName(v)}
                  </option>
                );
              })}
            </select>
            <select
              value={data.nyu_day}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (isNaN(value)) {
                  return;
                }
                data.nyu_day = value;
                setData({ ...data });
              }}
              disabled={disabled || props.isUpdate}
            >
              {options}
            </select>
            <span>日</span>
          </div>
        </FormItem>
        <FormItem
          label="振込時の名義人名"
          required
          tooltip={TOOLTIP_DATA.CORPS.corp_name_kana}
        >
          <input
            className="w-full"
            value={data.corp_name_kana}
            onChange={(e) => {
              data.corp_name_kana = e.target.value.replaceAll("ｰ", "-");
              setData({ ...data });
            }}
            disabled={disabled}
          />
        </FormItem>
      </div>

      <div className="mt-8 flex flex-col gap-2">
        <div className="pt-4 border-b font-bold">
          <div
            className="w-3/12 flex justify-between mb-2"
            style={{ whiteSpace: "pre-wrap" }}>
            口座登録
            <AppTooltip tooltip={TOOLTIP_DATA.CORPS.bank_account_registration} />
          </div>
        </div>
        {data.bank_account_list.map((v, i) => {
          return (
            <div
              key={i}
              className={`pt-2 pb-5 p-5 ${
                v?.is_duplicate
                  ? "rounded border-2 border-red-500"
                  : "border-dashed border-b-2"
              }`}
            >
              <div className="flex justify-end">
                {!disabled && i > 0 && (
                  <div
                    className="border border-black text-lg px-4 py-2 cursor-pointer"
                    onClick={() => onRemoveAccount(v)}
                  >
                    x
                  </div>
                )}
              </div>
              <AccountForms
                idx={i}
                data={v}
                branchMst={props.branchMst}
                onChange={() => {
                  setData({ ...data });
                }}
                onRefreshBankAcc={(is_JHK) => {
                  if (is_JHK) {
                    data.bank_account_list[i].bank_cd = JOHOKU_ACCOUNT.bank_cd;
                    data.bank_account_list[i].bank_name =
                      JOHOKU_ACCOUNT.bank_name;
                  } else {
                    data.bank_account_list[i].bank_cd = "";
                    data.bank_account_list[i].bank_name = "";
                  }

                  data.bank_account_list[i].branch_cd = "";
                  data.bank_account_list[i].branch_name = "";
                  setData({ ...data });
                }}
                disabled={disabled}
              />
              {!disabled && i === data.bank_account_list.length - 1 && (
                <div className="flex justify-start my-4">
                  <button
                    className="border border-black text-lg px-4 py-2  disabled:bg-gray-400"
                    onClick={onAddAccount}
                  >
                    +
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default CorpInfoForms;
